import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Webcam from 'react-webcam';
import { Camera, Image as ImageIcon, Mic, Send, X, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'motion/react';

interface InputSectionProps {
  onSubmit: (text: string, imageBase64?: string, mimeType?: string, language?: string) => void;
  isLoading: boolean;
}

const LANGUAGES = [
  "English",
  "Hindi",
  "Bengali",
  "Gujarati",
  "Kannada",
  "Malayalam",
  "Marathi",
  "Odia",
  "Punjabi",
  "Tamil",
  "Telugu",
  "Spanish",
  "French",
  "German",
  "Mandarin",
  "Japanese",
  "Korean",
  "Arabic",
  "Portuguese",
  "Russian",
  "Italian"
];

const LANGUAGE_CODES: Record<string, string> = {
  English: "en-US",
  Hindi: "hi-IN",
  Bengali: "bn-IN",
  Gujarati: "gu-IN",
  Kannada: "kn-IN",
  Malayalam: "ml-IN",
  Marathi: "mr-IN",
  Odia: "or-IN",
  Punjabi: "pa-IN",
  Tamil: "ta-IN",
  Telugu: "te-IN",
  Spanish: "es-ES",
  French: "fr-FR",
  German: "de-DE",
  Mandarin: "zh-CN",
  Japanese: "ja-JP",
  Korean: "ko-KR",
  Arabic: "ar-SA",
  Portuguese: "pt-PT",
  Russian: "ru-RU",
  Italian: "it-IT"
};

export function InputSection({ onSubmit, isLoading }: InputSectionProps) {
  const [text, setText] = useState('');
  const [image, setImage] = useState<{ data: string; mimeType: string } | null>(null);
  const [language, setLanguage] = useState('English');
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const webcamRef = useRef<Webcam>(null);

  // Speech Recognition States & Refs
  const [isListening, setIsListening] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [isSpeechSupported, setIsSpeechSupported] = useState(true);
  const recognitionRef = useRef<any>(null);
  const textBeforeSpeechRef = useRef('');

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSpeechSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.error("Error cleaning up speech recognition:", e);
        }
      }
    };
  }, []);

  const startListening = () => {
    setVoiceError(null);
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceError("Speech recognition is not supported in this browser. Please try Chrome, Edge, or Safari.");
      setIsSpeechSupported(false);
      return;
    }

    try {
      const rec = new SpeechRecognition();
      rec.continuous = true;
      rec.interimResults = true;
      
      const currentLangCode = LANGUAGE_CODES[language] || 'en-US';
      rec.lang = currentLangCode;

      textBeforeSpeechRef.current = text;

      rec.onstart = () => {
        setIsListening(true);
      };

      rec.onresult = (event: any) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }

        const currentText = textBeforeSpeechRef.current;
        const spacing = currentText && !currentText.endsWith(' ') ? ' ' : '';
        const speechOutput = finalTranscript || interimTranscript;
        
        setText(currentText + spacing + speechOutput);
      };

      rec.onerror = (event: any) => {
        console.error("Speech recognition error:", event.error);
        if (event.error === 'not-allowed') {
          setVoiceError("Microphone access was denied. Please allow microphone permissions in your browser.");
        } else if (event.error === 'no-speech') {
          setVoiceError("No speech detected. Please try speaking again.");
        } else {
          setVoiceError(`Voice recognition error: ${event.error}`);
        }
        stopListening();
      };

      rec.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = rec;
      rec.start();
    } catch (err) {
      console.error("Failed to start speech recognition:", err);
      setVoiceError("Could not start speech recognition.");
      setIsListening(false);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error("Error stopping speech recognition:", e);
      }
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  const toggleSpeechRecognition = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        // Extract base64 data and mime type
        const matches = base64String.match(/^data:(.+);base64,(.+)$/);
        if (matches && matches.length === 3) {
          setImage({ mimeType: matches[1], data: matches[2] });
        }
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxFiles: 1,
    multiple: false
  } as any);

  const captureCamera = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      const matches = imageSrc.match(/^data:(.+);base64,(.+)$/);
      if (matches && matches.length === 3) {
        setImage({ mimeType: matches[1], data: matches[2] });
      }
      setIsCameraOpen(false);
    }
  }, [webcamRef]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() && !image) return;
    if (isListening) {
      stopListening();
    }
    onSubmit(text, image?.data, image?.mimeType, language);
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl shadow-purple-900/20 border border-slate-200 overflow-hidden">
      <form onSubmit={handleSubmit} className="flex flex-col">
        {/* Text Input Area */}
        <div className="p-2 pb-0">
          <textarea
            id="question"
            rows={2}
            className="w-full rounded-xl border-none px-4 py-2 focus:outline-none resize-none text-slate-900 placeholder:text-slate-400 text-lg font-medium bg-transparent"
            placeholder="Ask Professor X anything..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={isLoading}
          />
        </div>

        {/* Voice Error Banner */}
        {voiceError && (
          <div className="flex items-center justify-between gap-2 px-6 py-2 bg-amber-50 border-b border-amber-100 text-amber-700 text-xs font-semibold">
            <span>{voiceError}</span>
            <button
              type="button"
              onClick={() => setVoiceError(null)}
              className="text-amber-500 hover:text-amber-700 font-bold px-1"
            >
              ✕
            </button>
          </div>
        )}

        {/* Pulsing Waveform Animation during listening */}
        {isListening && (
          <div className="flex items-center gap-3 px-6 py-2 bg-indigo-50/50 border-b border-indigo-100">
            <span className="text-xs font-semibold text-indigo-600 animate-pulse tracking-wide select-none">
              LISTENING ({language.toUpperCase()})...
            </span>
            <div className="flex items-end gap-[3.5px] h-6 mb-1">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <motion.div
                  key={i}
                  className="w-[3px] bg-indigo-600 rounded-full"
                  animate={{
                    height: ["8px", "24px", "8px"],
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    delay: i * 0.1,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Image Preview Area (Conditional) */}
        {image && (
          <div className="px-8 pb-4">
            <div className="relative inline-block group">
              <img 
                src={`data:${image.mimeType};base64,${image.data}`} 
                alt="Uploaded question" 
                className="max-h-40 rounded-xl border border-slate-200 shadow-sm" 
              />
              <button
                type="button"
                onClick={() => setImage(null)}
                className="absolute -top-2 -right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-all shadow-lg opacity-0 group-hover:opacity-100"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* Camera Area (Conditional) */}
        {isCameraOpen && (
          <div className="px-8 pb-4">
            <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-black max-w-md mx-auto">
              <Webcam
                audio={false}
                ref={webcamRef as any}
                screenshotFormat="image/jpeg"
                className="w-full h-auto object-contain"
                videoConstraints={{ facingMode: "user" }}
                onUserMedia={() => {}}
                onUserMediaError={() => {}}
                mirrored={false}
                imageSmoothing={true}
                forceScreenshotSourceSize={false}
                disablePictureInPicture={true}
                screenshotQuality={1}
              />
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                <button
                  type="button"
                  onClick={captureCamera}
                  className="bg-white text-slate-900 px-4 py-2 rounded-full text-sm font-bold hover:bg-slate-100 transition-colors shadow-lg flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" /> Capture
                </button>
                <button
                  type="button"
                  onClick={() => setIsCameraOpen(false)}
                  className="bg-slate-800 text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-slate-700 transition-colors shadow-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Control Row */}
        <div className="flex items-center justify-between gap-4 px-6 py-2 bg-slate-50/50 border-t border-slate-100">
          {/* Left: Action Controls */}
          <div className="flex items-center gap-3">
            <div {...getRootProps()}>
              <input {...getInputProps()} disabled={isLoading} />
              <button
                type="button"
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:border-indigo-400 hover:bg-indigo-50 transition-all disabled:opacity-50 h-[44px] min-w-[140px] justify-center shadow-sm cursor-pointer"
              >
                <ImageIcon className="w-4 h-4 text-indigo-500" />
                Upload Photo
              </button>
            </div>
            
            <button
              type="button"
              onClick={() => setIsCameraOpen(true)}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:border-indigo-400 hover:bg-indigo-50 transition-all disabled:opacity-50 h-[44px] min-w-[140px] justify-center shadow-sm cursor-pointer"
            >
              <Camera className="w-4 h-4 text-indigo-500" />
              Use Camera
            </button>

            <button
              type="button"
              onClick={toggleSpeechRecognition}
              disabled={isLoading || !isSpeechSupported}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-bold transition-all disabled:opacity-50 h-[44px] min-w-[140px] justify-center shadow-sm cursor-pointer",
                isListening 
                  ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100" 
                  : "bg-white border-slate-200 text-slate-700 hover:border-indigo-400 hover:bg-indigo-50"
              )}
              title={!isSpeechSupported ? "Speech recognition is not supported in this browser" : "Ask with Voice"}
            >
              <Mic className={cn("w-4 h-4", isListening ? "text-red-500 animate-pulse" : "text-indigo-500")} />
              {isListening ? "Stop Mic" : "Ask with Voice"}
            </button>
          </div>

          {/* Right: Language & Submit */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-1 h-[44px] shadow-sm">
              <Globe className="w-4 h-4 text-slate-400" />
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isLoading}
                className="bg-transparent text-slate-700 text-sm font-bold outline-none cursor-pointer min-w-[100px]"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
            
            <button
              type="submit"
              disabled={isLoading || (!text.trim() && !image)}
              className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed h-[44px] shadow-md shadow-indigo-200"
            >
              <Send className="w-4 h-4" />
              Generate Video Solution
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
